import ScreenFill from '../../display/ui/ScreenFill';
import IntroMysteryMachine from './sprites/IntroMysteryMachine';
import LocationBadmanRun from './sprites/LocationBadmanRun';
import StoryContinuesCTA from './sprites/StoryContinuesCTA';

const CustomClasses = {
  IntroMysteryMachine: IntroMysteryMachine,
  LocationBadmanRun: LocationBadmanRun,
  StoryContinuesCTA: StoryContinuesCTA,
};

export default class StoryAnimation extends Phaser.Group {
  constructor(game, animationData) {
    super(game);

    // track all layers and their key frames
    this.keyframedata = animationData;
    this.totalFrames = animationData.duration;
    this.animationWidth = animationData.width;
    this.animationHeight = animationData.height;
    // turn the layers into an array, this will make easier looping
    this.layers = Object.keys(this.keyframedata).map((key) => {
      return { layer: key, keyframes: this.keyframedata[key] };
    });
    // track last frame registered and how long animation has been running
    this.lastFrame = 0;
    this.lifeTime = 0;
    // track all active sprites in animation
    this.sprites = [];
    // let the world know we are done
    this.onComplete = new Phaser.Signal();

    // add container to house animation elements
    this.container = this.game.make.group();
    this.add(this.container);

    // add screen dafe
    this.fade = new ScreenFill(this.game, 0x000000);
    this.add(this.fade);

    // initialize first frame
    this.updateKeyFrame(0);

    // start fading in
    this.game.add
      .tween(this.fade)
      .to({ alpha: 0 }, 500)
      .start();
  }

  /*
  =======================================================
    UPDATE
  =======================================================
  */

  update() {
    super.update();

    // increment lifetime
    this.lifeTime += this.game.time.elapsedMS;
    // calculate current frame
    const currentFrame = Math.floor((this.lifeTime / 1000) * 24);
    // if current frame is not the same as last frame
    if (currentFrame > this.lastFrame) {
      // update all missed and current frames
      while (this.lastFrame != currentFrame) {
        this.lastFrame++;
        // update keyframes
        this.updateKeyFrame(this.lastFrame);
      }
    }

    // if not fading out and down to the last second, start fade
    if (!this.fadeout && this.totalFrames - currentFrame < 25) {
      this.fadeout = true;
      this.game.add
        .tween(this.fade)
        .to({ alpha: 1 }, 500)
        .start();
    }

    // if fcurrent frae atches totale frames, the animation is complete
    if (!this.complete && currentFrame >= this.totalFrames) {
      this.complete = true;
      this.onComplete.dispatch();
    }
  }

  /*
  =======================================================
    KEYFRAME UPDATE
  =======================================================
  */

  updateKeyFrame(index) {
    // track if a new asset has been added, we will sort layers if needed
    let assetAdded = false;

    // loop through each layer
    this.layers.forEach((layer) => {
      // search for a keyframe in the layer
      const keyframe = layer.keyframes[index.toString()];
      // if the layer has a keyframe
      if (keyframe) {
        // track sprite to save lookups
        let sprite = false;

        // ifthe key frame has an 'add' property, the layer wants us to add a new sprite
        if (keyframe.add) {
          assetAdded = true;
          if (keyframe.class) {
            sprite = new CustomClasses[keyframe.class](this.game, layer.layer);
            this.container.add(sprite);
            this.sprites.push(sprite);
          } else {
            sprite = this.addNewSprite(keyframe.add, layer.layer, keyframe.animated);
          }
        }

        // if we have not created a sprite, we need to find an existing one
        if (!sprite) {
          sprite = this.getSpriteByLayer(layer.layer);
        }

        // if we have a sprite, check what updates it needs
        if (sprite) {
          if (keyframe.remove) {
            this.removeSprite(sprite);
          }
          if (keyframe.swap) {
            if (keyframe.class) {
              const oldsprite = sprite;
              sprite = new CustomClasses[keyframe.class](this.game, layer.layer);
              sprite.x = oldsprite.x;
              sprite.y = oldsprite.y;
              this.removeSprite(oldsprite);
              this.container.add(sprite);
              this.sprites.push(sprite);
            } else {
              sprite.loadTexture(keyframe.swap);
            }
          }
          if (keyframe.position) {
            this.positionSprite(sprite, keyframe.position);
          }
          if (keyframe.tween) {
            this.tweenSprite(sprite, keyframe.tween);
          }
        }

        // play sound if needed
        if (keyframe.audiosprite) {
          if (keyframe.audiosprite.action == 'play') {
            console.log(index, keyframe.audiosprite.audio_id);
            this.game.sounds.playSpriteSFX(keyframe.audiosprite.audio_id, {
              spriteID: keyframe.audiosprite.sprite_id,
            });
          } else if (keyframe.audiosprite.action == 'stop') {
            this.game.sounds.stopSFX(keyframe.audiosprite.audio_id, {
              spriteID: keyframe.audiosprite.sprite_id,
            });
          }
        }
      }
    });

    // if a new asset was added, we need to resort layers
    if (assetAdded) {
      this.container.sort('layer', Phaser.Group.SORT_DESCENDING);
    }
  }

  getSpriteByLayer(layer) {
    return this.sprites.find((sprite) => {
      return sprite.layer === layer;
    });
  }

  /*
  =======================================================
    SPRITE UPDATE
  =======================================================
  */

  addNewSprite(frame, layer, animated) {
    const sprite = this.game.make.sprite(0, 0, frame);
    sprite.id = frame;
    sprite.layer = layer;
    if (animated) {
      sprite.animations.add('play');
      sprite.animations.play('play', 24, true);
    }
    this.container.add(sprite);
    this.sprites.push(sprite);
    return sprite;
  }

  removeSprite(sprite) {
    sprite.visible = false;
    // clean up any existing tweens
    this.clearSpriteTweens(sprite);
    // remove from group
    this.container.remove(sprite);
    // remove from list
    this.sprites.splice(this.sprites.indexOf(sprite), 1);
    // destroy
    sprite.destroy();
  }

  swapSprite(layer, newasset) {
    const sprite = this.getSpriteByLayer(layer);
    if (sprite) {
      sprite.loadTexture(newasset);
    }
  }

  positionSprite(sprite, positionData) {
    sprite.x = positionData.x;
    sprite.y = positionData.y;
    sprite.alpha = positionData.alpha;
    sprite.angle = positionData.angle || 0;
    sprite.scale.set(positionData.scaleX, positionData.scaleY);
  }

  tweenSprite(sprite, tweenData) {
    // figureout ease type
    let ease = null;
    if (tweenData.ease > 0) {
      ease = Phaser.Easing.Quintic.Out;
    } else if (tweenData.ease > 0) {
      ease = Phaser.Easing.Quintic.In;
    }

    // clean up any existing tweens
    this.clearSpriteTweens(sprite);

    // create new position tween
    sprite.tweenPos = this.game.add
      .tween(sprite)
      .to(
        { x: tweenData.x, y: tweenData.y, alpha: tweenData.alpha },
        tweenData.duration,
        ease,
        true
      );

    // create new scale tween
    sprite.tweenScale = this.game.add
      .tween(sprite.scale)
      .to({ x: tweenData.scaleX, y: tweenData.scaleY }, tweenData.duration, ease, true);
  }

  clearSpriteTweens(sprite) {
    if (sprite.tweenScale) {
      sprite.tweenScale.stop();
    }
    if (sprite.tweenPos) {
      sprite.tweenPos.stop();
    }
  }

  destroy() {
    this.sprites.forEach((sprite) => {
      this.removeSprite(sprite);
    });
    this.container.destroy(true);
    this.fade.destroy();
    this.onComplete.removeAll();
    this.sprites = null;
    this.container = null;
    this.fade = null;
    this.onComplete = null;
    this.fadeout = null;
    this.complete = null;
    this.lastFrame = null;
    this.lifeTime = null;
    this.keyframedata = null;
    this.totalFrames = null;
    this.animationWidth = null;
    this.animationHeight = null;
    super.destroy(true);
  }
}



// WEBPACK FOOTER //
// ./js/app/ui/story/StoryAnimation.js